import * as Sentry from '@sentry/react';
import ENV from './env.json';

/**
 * Check if running on localhost (local developer machine).
 */
const isLocalhost = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

/**
 * Check if Sentry should be force-enabled when running locally.
 * Enable via: localStorage.setItem('sentry_debug', 'true')
 * Or via URL: ?sentry_debug=true
 */
const isSentryDebugEnabled = () => {
  if (!isLocalhost()) return false;
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('sentry_debug') === 'true') {
    localStorage.setItem('sentry_debug', 'true');
    return true;
  }
  return localStorage.getItem('sentry_debug') === 'true';
};

/**
 * Initialize Sentry error tracking.
 * Must be called as early as possible in the application lifecycle.
 * @param dsnOverride - Optional DSN to use instead of the default. Useful for
 *   separate Sentry projects (e.g. standalone app vs admin app).
 */
export const initSentry = (dsnOverride?: string) => {
  const dsn = dsnOverride || ENV.sentry?.dsn;

  // Skip initialization if no DSN is configured
  if (!dsn) {
    console.warn('Sentry DSN not configured, error tracking disabled');
    return;
  }

  const debugEnabled = isSentryDebugEnabled();
  if (debugEnabled) {
    console.log('[Sentry] Debug mode enabled - events will be sent to Sentry');
  }

  Sentry.init({
    dsn,
    environment: ENV.env.name,
    enabled: !isLocalhost() || debugEnabled,
    release: import.meta.env.GIT_SHA,

    // Send default PII data (IP address, etc.)
    sendDefaultPii: true,

    // Basic error tracking only - no performance monitoring
    tracesSampleRate: 0,

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      /extensions\//i,
      /^chrome-extension:\/\//,
      // Network errors that aren't actionable
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Firebase auth errors that are expected
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request'
    ],

    beforeSend(event) {
      // Don't send events when running locally (unless debug enabled)
      if (isLocalhost() && !debugEnabled) {
        console.log('[Sentry] Would send event:', event);
        return null;
      }
      return event;
    }
  });
};

/**
 * Set the current user context for Sentry.
 * Call this after authentication.
 */
export const setSentryUser = (
  userId: string,
  email?: string,
  username?: string
) => {
  Sentry.setUser({
    id: userId,
    email,
    username
  });
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTag('org_id', undefined as any);
  Sentry.setTag('org_name', undefined as any);
};

/**
 * Set the current organization context for Sentry.
 * Call this after authentication and when org changes (org switcher).
 */
export const setSentryOrg = (orgId: string, orgName?: string) => {
  Sentry.setTag('org_id', orgId);
  if (orgName) {
    Sentry.setTag('org_name', orgName);
  }
};

/**
 * Capture an exception manually with optional context.
 */
export const captureException = (
  error: Error | unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  }
) => {
  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    if (context?.level) {
      scope.setLevel(context.level);
    }
    Sentry.captureException(error);
  });
};

/**
 * Capture a message (non-error) with optional context.
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Add a breadcrumb for debugging context.
 * Breadcrumbs are included with error reports to show what happened before the error.
 */
export const addBreadcrumb = (breadcrumb: {
  category: string;
  message: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}) => {
  Sentry.addBreadcrumb({
    category: breadcrumb.category,
    message: breadcrumb.message,
    level: breadcrumb.level || 'info',
    data: breadcrumb.data
  });
};

// Re-export ErrorBoundary for use in React components
export { ErrorBoundary } from '@sentry/react';
